import {
  ENTRY_FEE_PGA_S5,
  FIRST_PLACE_PRIZE_PGA_S5,
  INITIAL_PURSE_PGA_S5,
  MIN_PRIZE_PGA_S5,
  PAID_PLACES_PCT_PGA_S5,
  PRIZE_SCALING_PGA_S5,
  SIDE_GAMES_PGA_S5,
} from "@/utils/constants";

/**
 * PGA of America, season 5.
 *
 *   pool     = purse + players x fee - first place - side games
 *   M        = trunc(players x 10%) paid places (under 2: winner only)
 *   scale(j) = max(1, 10 x (1 - scaling)^(j - 2))        for j >= 2
 *   weight(j)= scale(j) x (M - j + 1)
 *
 * Rank 1 is fixed at FIRST_PLACE_PRIZE. Ranks 3..M take their weighted
 * share of the pool, floored at MIN_PRIZE. Rank 2 absorbs whatever is left
 * (also floored at MIN_PRIZE), so whole-dollar prizes total the pool
 * exactly without rounding drift.
 *
 * Fields under 20 players pay the winner only (matches the finance sheet).
 *
 * @returns Prize per rank, index 0 = 1st, length playersCount, unpaid = 0.
 */
export const prizeCalculatorPGAAmericaS5 = (
  playersCount: number,
): number[] => {
  if (!Number.isSafeInteger(playersCount) || playersCount <= 0) return [];

  const prizes = new Array<number>(playersCount).fill(0);
  prizes[0] = FIRST_PLACE_PRIZE_PGA_S5;

  const paidPlaces = Math.min(
    playersCount,
    Math.trunc(playersCount * PAID_PLACES_PCT_PGA_S5),
  );

  // Fewer than 2 paid places (under 20 players): only the winner is paid.
  if (paidPlaces < 2) return prizes;

  const pool =
    INITIAL_PURSE_PGA_S5 +
    ENTRY_FEE_PGA_S5 * playersCount -
    FIRST_PLACE_PRIZE_PGA_S5 -
    SIDE_GAMES_PGA_S5;

  const weightFor = (rank: number) =>
    (paidPlaces - rank + 1) *
    Math.max(1, 10 * Math.pow(1 - PRIZE_SCALING_PGA_S5, rank - 2));

  let totalWeight = 0;
  for (let rank = 2; rank <= paidPlaces; rank++) {
    totalWeight += weightFor(rank);
  }

  // Round ranks 3..M first so second place settles on whole dollars.
  let awarded = 0;
  for (let rank = 3; rank <= paidPlaces; rank++) {
    const prize = Math.round(
      Math.max(MIN_PRIZE_PGA_S5, (weightFor(rank) / totalWeight) * pool),
    );
    prizes[rank - 1] = prize;
    awarded += prize;
  }

  // Second place absorbs rounding and minimum-prize adjustments.
  prizes[1] = Math.max(MIN_PRIZE_PGA_S5, pool - awarded);

  return prizes;
};
